import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageTransition } from '../components/ui/PageTransition';
import { SEO } from '../components/shared/SEO';
import { AnimatedSection } from '../components/ui/AnimatedSection';
import { API_ENDPOINTS } from '../config/api';
import { Footer } from '../components/layout/Footer';
import { motion } from 'framer-motion';
import { useSharedElement } from '../contexts/SharedElementContext';
import { SharedElement } from '../components/ui/SharedElement';
import { useSharedElementId } from '../hooks/useSharedElementId';

interface Product {
  _id: string;
  name: string;
  price: number;
  image_url: string;
  description: string[];
  sort_id: number;
}

export function Store() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { setSharedElementId } = useSharedElement();
  useSharedElementId({ id: undefined });

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.store.products);
        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }
        const data = await response.json();
        setProducts(data.sort((a: Product, b: Product) => a.sort_id - b.sort_id));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load products');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleProductClick = (productId: string) => {
    setSharedElementId(productId);
    navigate(`product/${productId}`);
  };

  return (
    <PageTransition>
      <SEO
        title={t('seo.store.title')}
        description={t('store.description', 'Get premium points to unlock exclusive features and support the server.')}
      />
      <div className="min-h-screen bg-gray-900">
        <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-up">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h1 className="text-4xl sm:text-5xl font-bold mb-4">
                <span className="bg-gradient-to-r from-primary-400 to-yellow-300 text-transparent bg-clip-text">
                  {t('store.title', 'Premium Points Store')}
                </span>
              </h1>
              <p className="text-lg text-gray-300">
                {t('store.subtitle', 'Purchase Premium Points to unlock exclusive features')}
              </p>
            </div>
          </AnimatedSection>

          {loading ? (
            <div className="flex justify-center">
              <div className="relative">
                <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-700 border-t-primary-500"></div>
                <div className="absolute inset-0 animate-spin rounded-full h-12 w-12 border-2 border-transparent border-b-primary-500" style={{ animationDirection: 'reverse', animationDuration: '1s' }}></div>
              </div>
            </div>
          ) : error ? (
            <div className="text-red-500 text-center p-4 bg-red-500/10 rounded-lg border border-red-500/20 max-w-lg mx-auto">
              {error}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {products.map((product) => (
                <AnimatedSection
                  key={product._id}
                  animation="fade-up"
                  className="group"
                >
                  <div 
                    className="bg-gray-800/50 backdrop-blur-sm rounded-xl overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl border border-gray-700/50 hover:border-primary-500/50"
                    onClick={() => handleProductClick(product._id)}
                  >
                    <div className="p-6">
                      <div className="aspect-square relative overflow-hidden rounded-lg bg-gray-900/50">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <SharedElement
                          id={`product-image-${product._id}`}
                          type="img"
                          src={product.image_url}
                          alt={product.name}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      </div>
                      <div className="mt-6">
                        <SharedElement 
                          id={`product-name-${product._id}`}
                          type="h3"
                          className="text-xl font-semibold text-white text-center group-hover:text-primary-400 transition-colors duration-300"
                        >
                          {product.name}
                        </SharedElement>
                        <div className="w-12 h-0.5 bg-primary-500/30 mx-auto mt-3 rounded-full transform origin-left transition-all duration-300 group-hover:w-24 group-hover:bg-primary-500/50" />
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          )}
        </div>
        <div className="text-center mb-8">
          <p className="text-gray-400 hover:text-primary-400 transition-colors">
            {t('store.trouble')} {" "}
            <a 
              href="https://trakteer.id/kaizenmc" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary-400 hover:text-primary-300 underline"
            >
              https://trakteer.id/kaizenmc
            </a>
          </p>
        </div>
      </div>
      <Footer />
    </PageTransition>
  );
} 